/**
 * PlatformHotels — list of hotels + link to Create Hotel.
 * ADR-WF-1 v2.1 §26 + §37.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, UserPlus, ChevronRight, AlertTriangle, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";

const STATUS_STYLE = {
  draft:       { label: "Draft",       cls: "bg-slate-100 text-slate-700 border-slate-300" },
  trial:       { label: "Trial",       cls: "bg-amber-50 text-amber-700 border-amber-200" },
  active:      { label: "Active",      cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  suspended:   { label: "Suspended",   cls: "bg-rose-50 text-rose-700 border-rose-200" },
  archived:    { label: "Archived",    cls: "bg-slate-100 text-slate-500 border-slate-300" },
  soft_deleted:{ label: "Deleted",     cls: "bg-slate-100 text-slate-400 border-slate-200" },
};

export default function PlatformHotels() {
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);

  const load = async (includeDeleted) => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/v1/hotels", { params: { include_deleted: includeDeleted } });
      setHotels(data || []);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.response?.data?.message;
      setErr(detail || (status ? `Failed to load hotels (HTTP ${status})` : "Failed to load hotels — network error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(showDeleted);
  }, [showDeleted]);

  const deletedCount = hotels.filter((h) => h.status === "soft_deleted").length;

  return (
    <div className="space-y-8" data-testid="platform-hotels-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[12px] uppercase tracking-[0.14em] text-amber-600 font-semibold">
            Platform
          </div>
          <h1 className="text-3xl font-semibold text-slate-900 mt-1">Hotels</h1>
          <p className="text-slate-600 mt-2 text-sm">
            Every hotel on the HOCP platform.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setShowDeleted((v) => !v)}
            data-testid="platform-hotels-show-deleted-toggle"
            className={`inline-flex items-center gap-2 px-3.5 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${
              showDeleted
                ? "bg-hocp-mint border-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong"
                : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
            }`}
          >
            {showDeleted ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {showDeleted ? "Showing deleted" : "Show deleted"}
            {showDeleted && deletedCount > 0 && (
              <span className="ml-0.5 text-[11px] font-bold px-1.5 py-0.5 rounded bg-white/20">{deletedCount}</span>
            )}
          </button>
          <Link
            to="/platform/hotels/new"
            data-testid="platform-hotels-create-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500 text-white font-semibold text-sm hover:bg-amber-600 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Create Hotel
          </Link>
        </div>
      </header>

      {err && (
        <div
          className="rounded-lg bg-rose-50 border border-rose-200 p-4 text-rose-800 text-sm flex items-start gap-2"
          data-testid="platform-hotels-error"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{err}</span>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm">
        <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)_100px_100px_120px_40px] items-center px-6 py-3 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-semibold bg-slate-50">
          <div>Hotel</div>
          <div>Organization</div>
          <div>Status</div>
          <div>Admin</div>
          <div>Setup</div>
          <div></div>
        </div>
        {loading ? (
          <div className="p-6 text-slate-500" data-testid="platform-hotels-loading">
            Loading…
          </div>
        ) : hotels.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="platform-hotels-empty">
            No hotels yet. Click <strong className="text-amber-600">Create Hotel</strong> to
            onboard your first one.
          </div>
        ) : (
          hotels.map((h) => {
            const status = STATUS_STYLE[h.status] || STATUS_STYLE.draft;
            return (
              <Link
                key={h.id}
                to={
                  h.status === "soft_deleted" || h.has_administrator
                    ? `/platform/hotels/${h.id}`
                    : `/platform/hotels/${h.id}/administrator`
                }
                data-testid={`platform-hotel-row-${h.id}`}
                className="grid grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)_100px_100px_120px_40px] items-center px-6 py-4 border-b border-slate-200 last:border-b-0 hover:bg-slate-50 transition-colors group"
              >
                <div className="min-w-0">
                  <div className="text-slate-900 font-medium truncate">{h.name}</div>
                  <div className="text-xs text-slate-500 truncate mono">{h.code}</div>
                </div>
                <div className="text-sm text-slate-700 truncate">
                  {h.organization_name || <span className="text-slate-400">—</span>}
                </div>
                <div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-semibold ${status.cls}`}>
                    {status.label}
                  </span>
                </div>
                <div>
                  {h.has_administrator ? (
                    <span className="text-xs text-emerald-700 font-medium">Assigned</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-700 font-medium">
                      <UserPlus className="w-3.5 h-3.5" />
                      Missing
                    </span>
                  )}
                </div>
                <div className="text-sm text-slate-700 tabular-nums">
                  {h.setup_progress_pct}%
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
}
